import type { RouteId } from "@/routing/routes";

/**
 * Where the botanical atmosphere is allowed to gather.
 *
 * One declaration per route, keyed to existing section anchors. The renderer
 * adds the shared `veil-host` class to these elements; all geometry, masking
 * and density live in src/styles/botanical-veil.css.
 *
 * Placement rules held here: only a route's most meaningful entrance,
 * transition or synthesis moments, alternating edges, with long stretches of
 * clean ivory between appearances. Dense procedural copy and the evidence
 * register are deliberately left clear.
 */
export const VEIL_HOSTS: Record<RouteId, readonly string[]> = {
  // Worldview map side, then the four supporting fields as the route closes
  field: ["field-section-1", "field-section-3"],
  // Entrance, then the gift-and-limit synthesis
  stellar: ["stellar-section-1", "stellar-section-4"],
  // The stewardship / readiness threshold, then a quiet close
  earth2: ["earth2-section-5", "earth2-section-7"],
  // The contrast turn, then the reality-check close
  stars: ["stars-section-2", "stars-section-5"],
  // Gold master: opening canopy, Transition Commons, the deeper governance
  // threshold, and the closing arrival
  earth3: ["earth3-premise", "earth3-commons", "earth3-decisions", "earth3-proof"],
  // One deeper operating transition; the 90-day procedure stays clear
  practice: ["practice-section-4", "practice-section-7"],
  // Quietest route: a restrained entrance veil and a soft closing threshold
  sources: ["sources-section-1", "sources-section-6"],
};

export function veilHostsFor(route: RouteId): ReadonlySet<string> {
  return new Set(VEIL_HOSTS[route]);
}
